import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import type L from "leaflet";
import type { LatLng } from "leaflet";
import {
  CanvasPinMarker,
  PIN_DRAW_LEVEL,
} from "../../utils/canvasPin/CanvasPinMarker";
import { getSharedCanvasPinRenderer } from "../../utils/canvasPin/sharedCanvasPinRenderer";
import {
  PIN_COLORS,
  getTeardropPinIcon,
} from "../../utils/canvasPin/teardropPin";

interface ListedPropertyPinsLayerProps {
  /** 物件一覧の物件の位置 */
  positions: LatLng[];
  /** 物件一覧で選んでいる行の位置。そのピンだけ選択中の見た目（大きく、外縁付き）で一番上に描く */
  focusedPosition?: LatLng | null;
  /** 青ピンを押した時。押した青ピンの地点を渡す */
  onPinClick?: (position: LatLng, event: L.LeafletMouseEvent) => void;
}

/** 同じ位置のピンを使い回すためのキー */
const positionKey = (latlng: LatLng): string => `${latlng.lat},${latlng.lng}`;

/**
 * 物件一覧に入っている物件を、青いしずく型のピンで示すレイヤー。
 *
 * 検索結果・保存した物件リストなど元のピンがある物件は、その上に重ねて描くことで
 * 「一覧に入ると青に変わる」ように見せる。一覧から消すと青ピンが消え、元の色に戻る。
 *
 * 青ピンは元のピンの上に描くので、クリックも青ピンが受け取り onPinClick に渡す
 * （下に元のピンがあればそれを押したことにする、などは呼び出し側で決める）。
 * 受け取らないと、ピンの頭は地点より上に描かれているため、クリックが地図に抜けて
 * 地点からずれた場所（隣の筆）で地図クリックの検索が走ってしまう。
 */
export const ListedPropertyPinsLayer: React.FC<
  ListedPropertyPinsLayerProps
> = ({ positions, focusedPosition = null, onPinClick }) => {
  const map = useMap();
  const markersRef = useRef<Map<string, CanvasPinMarker>>(new Map());
  // ピンは使い回すので、クリックの関数は ref で最新のものを呼ぶ
  const onPinClickRef = useRef(onPinClick);
  onPinClickRef.current = onPinClick;

  useEffect(() => {
    const markers = markersRef.current;
    const nextKeys = new Set<string>();
    positions.forEach((latlng) => {
      const key = positionKey(latlng);
      nextKeys.add(key);
      if (markers.has(key)) return;
      const marker = new CanvasPinMarker(latlng, {
        icon: getTeardropPinIcon(PIN_COLORS.listed),
        selectedIcon: getTeardropPinIcon(PIN_COLORS.listed, { selected: true }),
        drawLevel: PIN_DRAW_LEVEL.listed,
        renderer: getSharedCanvasPinRenderer(map),
      })
        .on("click", (event: L.LeafletMouseEvent) => {
          onPinClickRef.current?.(latlng, event);
        })
        .addTo(map);
      markers.set(key, marker);
    });
    markers.forEach((marker, key) => {
      if (nextKeys.has(key)) return;
      marker.remove();
      markers.delete(key);
    });
  }, [map, positions]);

  // 一覧で選んでいる行のピンを選択中の見た目にする（ピンが作り直された時も付け直す）
  useEffect(() => {
    const focusedKey = focusedPosition ? positionKey(focusedPosition) : null;
    markersRef.current.forEach((marker, key) => {
      marker.setSelected(key === focusedKey);
    });
  }, [positions, focusedPosition]);

  // 地図から外れる時にすべて消す
  useEffect(() => {
    const markers = markersRef.current;
    return () => {
      markers.forEach((marker) => marker.remove());
      markers.clear();
    };
  }, [map]);

  return null;
};
